import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import Layout from 'containers/Layout'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { OfficeBuildingIcon, TruckIcon, UserIcon, LocationMarkerIcon } from '@heroicons/react/outline'
import { preBookingApiGetById } from 'lib/network/apiGet'
import PreBookingStatusBadge from './PreBookingStatusBadge'
import { PreBookingNotes } from './PreBookingNotes'
import { isGuestIntent, ownerLabel, summariseReminders, formatWallClockDateTime } from './preBookingHelpers'

const Card = ({ title, description, children, className = '' }) => (
  <section className={`flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm ${className}`}>
    {(title || description) && (
      <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">
        {title ? <h3 className="text-sm font-semibold text-gray-900">{title}</h3> : null}
        {description ? <p className="mt-0.5 text-xs text-gray-500">{description}</p> : null}
      </div>
    )}
    <div className="flex-1 p-4 sm:p-5">{children}</div>
  </section>
)

const Field = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 min-w-0">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm font-medium text-gray-900 break-words">{value ?? '-'}</span>
  </div>
)

const formatDateTime = value => (value ? moment(value).format('DD MMM YYYY · HH:mm') : '-')
const formatRelative = value => (value ? moment(value).fromNow() : '-')

const getCarImageUrl = car => car?.carImages?.[0]?.url || car?.carImages?.[0]?.thumbnailUrl || ''

const getCarTitle = car => [car?.car?.make?.make, car?.car?.model?.modal, car?.modelYear].filter(Boolean).join(' ') || '-'

const formatLocation = location => {
  if (!location) return '-'
  if (location.address) return location.address
  if (location.lat != null && location.lon != null) return `${location.lat}, ${location.lon}`
  return '-'
}

const PhoneList = ({ phones, empty = '-' }) => {
  if (!phones?.length) return empty
  return (
    <div className="space-y-1">
      {phones.map((phone, index) => (
        <div key={`${phone.phone}-${index}`} className="text-sm font-medium text-gray-900">
          {phone.title ? <span className="text-gray-500 font-normal">{phone.title}: </span> : null}
          {phone.phone || '-'}
        </div>
      ))}
    </div>
  )
}

const PreBookingDetail = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [intent, setIntent] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchIntent = async () => {
      setLoading(true)
      try {
        const res = await preBookingApiGetById(id)
        setIntent(res?.data?.intent || null)
      } finally {
        setLoading(false)
      }
    }
    fetchIntent()
  }, [id])

  if (loading) {
    return (
      <Layout title={t('preBookings.detailTitle')}>
        <div className="h-[220px] flex items-center justify-center">
          <LoadingIcon />
        </div>
      </Layout>
    )
  }

  if (!intent) {
    return (
      <Layout title={t('preBookings.detailTitle')}>
        <div className="py-10">
          <EmptyState v2 description={t('common.noData')} />
        </div>
      </Layout>
    )
  }

  const car = intent.carId
  const company = car?.company
  const branch = car?.branch
  const user = intent.userId
  const carTitle = getCarTitle(car)
  const carImage = getCarImageUrl(car)
  const reminders = summariseReminders(intent.remindersSent)
  const booking = intent.confirmedBookingId
  const guest = isGuestIntent(intent)

  return (
    <Layout title={t('preBookings.detailTitle')} breadcrumbs={[{ name: t('preBookings.breadcrumb'), pathname: '/pre-bookings' }]}>
      <div className="p-4 max-w-6xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm sm:px-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-gray-900 truncate">{carTitle}</h2>
              <PreBookingStatusBadge status={intent.status} t={t} />
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              {t('preBookings.columns.lastActivity')}: {formatRelative(intent.lastActivityAt)}
              {company?.name ? ` · ${company.name}` : ''}
            </p>
          </div>
          {booking?._id ? (
            <Link to={`/booking/${booking._id}`} className="inline-flex items-center rounded-md bg-dark-600 px-3 py-2 text-xs font-semibold text-white hover:bg-dark-700">
              {t('preBookings.actions.openBooking')}
            </Link>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card title={t('preBookings.sections.car')}>
            <div className="flex gap-4">
              <div className="relative h-28 w-36 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
                {carImage ? (
                  <img src={carImage} alt={carTitle} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <TruckIcon className="h-10 w-10 text-gray-300" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{carTitle}</p>
                  {car?._id ? (
                    <Link to={`/car/edit/${car._id}/car-profile`} className="mt-0.5 inline-block text-xs text-primary-600 hover:underline">
                      {t('preBookings.actions.openCar')}
                    </Link>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label={t('preBookings.fields.registrationNumber')} value={car?.registrationNumber || '-'} />
                  <Field label={t('preBookings.fields.modelYear')} value={car?.modelYear || '-'} />
                </div>
              </div>
            </div>
          </Card>

          <Card title={t('preBookings.sections.company')}>
            <div className="flex gap-4">
              <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-50">
                {company?.logoUrl ? (
                  <img src={company.logoUrl} alt={company?.name || ''} className="h-full w-full object-contain p-1" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <OfficeBuildingIcon className="h-7 w-7 text-gray-300" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{company?.name || '-'}</p>
                  {company?._id ? (
                    <Link to={`/company/edit/${company._id}/company-profile`} className="mt-0.5 inline-block text-xs text-primary-600 hover:underline">
                      {t('preBookings.actions.openCompany')}
                    </Link>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Field label={t('preBookings.fields.email')} value={company?.email || '-'} />
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="text-xs text-gray-500">{t('preBookings.fields.phone')}</span>
                    <PhoneList phones={company?.phones} />
                  </div>
                  <Field label={t('preBookings.fields.branch')} value={branch?.name || '-'} />
                  <Field label={t('preBookings.fields.address')} value={company?.address || '-'} />
                </div>
              </div>
            </div>
          </Card>

          <Card title={t('preBookings.sections.customer')}>
            <div className="flex gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100">
                <UserIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="min-w-0 flex-1 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={t('preBookings.columns.customer')} value={ownerLabel(intent, t('preBookings.guest'))} />
                <Field label={t('preBookings.fields.customerType')} value={guest ? t('preBookings.guest') : t('preBookings.signedIn')} />
                <Field label={t('preBookings.fields.email')} value={user?.email || '-'} />
                <Field label={t('preBookings.fields.phone')} value={user?.phone || '-'} />
                {guest ? <Field label={t('preBookings.fields.sessionId')} value={intent.sessionId || '-'} /> : null}
                <Field label={t('preBookings.columns.pushCapable')} value={intent.pushCapable ? t('common.yes') : t('common.no')} />
              </div>
            </div>
          </Card>

          <Card title={t('preBookings.sections.trip')}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t('preBookings.fields.pickup')} value={formatWallClockDateTime(intent.pickupDate)} />
              <Field label={t('preBookings.fields.dropOff')} value={formatWallClockDateTime(intent.returnDate)} />
              <Field label={t('preBookings.fields.deliveryType')} value={intent.deliveryType || '-'} />
              <Field label={t('preBookings.fields.insurance')} value={intent.insuranceType || '-'} />
              <Field label={t('preBookings.fields.coupon')} value={intent.couponCode || '-'} />
              <Field label={t('preBookings.fields.timeZone')} value={intent.timeZone || '-'} />
              <div className="sm:col-span-2 flex gap-2 rounded-md border border-gray-100 bg-gray-50 p-3">
                <LocationMarkerIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 w-full">
                  <Field label={t('preBookings.fields.pickupLocation')} value={formatLocation(intent.pickupLocation)} />
                  <Field label={t('preBookings.fields.dropOffLocation')} value={formatLocation(intent.dropOffLocation)} />
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card title={t('preBookings.sections.summary')}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label={t('preBookings.columns.created')} value={formatDateTime(intent.createdAt)} />
              <Field label={t('preBookings.columns.lastActivity')} value={formatRelative(intent.lastActivityAt)} />
              <Field label={t('preBookings.fields.abandonedAt')} value={formatDateTime(intent.abandonedAt)} />
              <Field label={t('preBookings.fields.confirmedAt')} value={formatDateTime(intent.confirmedAt)} />
            </div>
          </Card>

          {booking ? (
            <Card title={t('preBookings.sections.booking')}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={t('preBookings.fields.bookingStatus')} value={booking.status || '-'} />
                <Field label={t('preBookings.fields.paymentStatus')} value={booking.paymentStatus || '-'} />
                <Field label={t('preBookings.fields.totalPrice')} value={booking.totalPrice ?? '-'} />
                <div className="flex items-end">
                  <Link to={`/booking/${booking._id}`} className="text-sm font-medium text-primary-600 hover:underline">
                    {t('preBookings.actions.openBooking')}
                  </Link>
                </div>
              </div>
            </Card>
          ) : (
            <Card title={t('preBookings.sections.booking')}>
              <p className="text-sm text-gray-500">{t('preBookings.noConfirmedBooking')}</p>
            </Card>
          )}
        </div>

        <Card title={t('preBookings.sections.notes')} description={t('preBookings.notes.description')}>
          <PreBookingNotes intentId={intent._id} t={t} />
        </Card>

        <Card title={t('preBookings.sections.reminders')} description={t('preBookings.remindersDisclaimer')}>
          {reminders.length === 0 ? (
            <p className="text-sm text-gray-500">{t('preBookings.noReminders')}</p>
          ) : (
            <div className="space-y-2">
              {reminders.map(reminder => (
                <div key={reminder.stage} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="mb-1 flex items-center justify-between gap-3">
                    <span className="inline-flex rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">{reminder.stage}</span>
                    <span className="text-xs text-gray-500">{formatDateTime(reminder.sentAt)}</span>
                  </div>
                  <p className="text-sm text-gray-800">{reminder.channels.join(', ') || '-'}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Layout>
  )
}

export default PreBookingDetail
